import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertCircle, Check, Plus, Save, Search } from '../../icons';
import {
  listEmployees, getEmployee, updateEmployeeHr, hireEmployee,
  EMPLOYMENT_TYPE_LABEL, EMPLOYMENT_STATUS_LABEL,
  type EmployeeRowDto, type EmployeeDetailDto,
} from '../../api/hr-employee';
import { listWorkSchedules } from '../../api/hr-schedule';
import { JobGradeSelect } from '../../components/JobGradeSelect';
import './AttendanceAdmin.css';
// 기준 설정 (2026-08-28 · 판 7) — 고용형태의 «이름»과 «씀 여부»는 회사가 정한다.
//   못 읽으면 기본 목록(EMPLOYMENT_TYPE_LABEL)이 그대로 산다.
import { useCompanyCodes } from '../../lib/company-codes';
// 2026-09-13 · 계약 §17 — 이름 · 영문 이름 · 생일 · 사진은 본인이 못 고치고 **인사가 여기서** 고친다.
//   영문 이름 · 생일 · 조회 범위는 사람 카드(`GET /hr/people/:id`)가 준다. 사진 단추는 사람 머리가 한 벌로 갖고 있다.
import { fetchPersonCard, type PersonCardDto } from '../../api/hr-people';
import { PersonHead } from '../HrMenu/PersonHead';

/** 생일은 연도 없는 `MM-DD` — 서버·본인 경로와 같은 규칙. */
const BIRTHDAY_RE = /^(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/;
type IdentityKey = 'name' | 'nameEn' | 'birthday';

/**
 * 화면 A — **직원 관리**(인사와 계정을 한 화면에서) · 화면 B — **입사 처리**. 근태 3단계, 2026-08-24.
 *
 * 설계: `docs/attendance-round2-sample.html` 화면 A · B · 제안 1.
 *
 *   "화면을 나누지 말고 동작을 나눈다. 화면을 둘로 갈라 놓으면 입사 처리를 두 번 하게 되고,
 *    결국 누군가는 ADMIN 을 하나 더 받는다."
 *
 * 그래서 한 화면에서 세 묶음을 **같이 보되**, 고치는 것은 묶음마다 다르다. 무엇이 열리는지는
 * **서버가 판정해서 준다**(`editable`) — 화면이 스스로 정하면 서버와 어긋난다.
 *
 * ★ 계정 칸은 여기서 **보여만 준다**. 고치는 길은 관리 콘솔 하나여야 감사 기록이 갈리지 않는다.
 * ★ 초기 비밀번호는 만든 **그 순간 한 번**만 보인다 — 저장하지 않으므로 다시 볼 수 없다.
 */

const ERRORS: Record<string, string> = {
  ACCOUNT_FIELD_NOT_HERE: '계정 칸은 관리 콘솔의 사용자 관리에서 고칩니다.',
  EMAIL_EXISTS: '이미 있는 이메일입니다.',
  EMAIL_EXISTS_DELETED: '지운 계정이 같은 이메일을 쓰고 있습니다 — 관리 콘솔에서 되살려 주세요.',
  EMAIL_INVALID: '이메일 형식이 아닙니다.',
  INVALID_DATE: '없는 날짜입니다.',
  FORBIDDEN: '이 화면은 회사 관리자만 쓸 수 있습니다.',
  FST_ERR_VALIDATION: '입력 형식이 올바르지 않습니다 — 이름은 비울 수 없고, 생일은 MM-DD 입니다.',
};

interface HireForm {
  name: string; email: string; employeeNumber: string; hireDate: string;
  position: string; employmentType: string; teamId: string;
}

const EMPTY_HIRE: HireForm = {
  name: '', email: '', employeeNumber: '', hireDate: '',
  position: '', employmentType: 'REGULAR', teamId: '',
};

export function EmployeesPage(): JSX.Element {
  // ★ 고용형태 이름은 회사가 정한다(「단시간」을 「시간제」로 부르는 곳이 있다).
  const codes = useCompanyCodes('hr');
  const employmentTypes = codes.apply(
    'employment_type',
    Object.entries(EMPLOYMENT_TYPE_LABEL).map(([key, label]) => ({ key, label })),
  );

  const [rows, setRows] = useState<EmployeeRowDto[]>([]);
  const [missing, setMissing] = useState(0);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [detail, setDetail] = useState<EmployeeDetailDto | null>(null);
  const [hrDraft, setHrDraft] = useState<Record<string, string>>({});
  /** 사람 카드 — 못 읽으면(옛 서버 404) `null` 이고, 그때는 영문 이름 · 생일을 잠근다(지금 값을 모르므로). */
  const [card, setCard] = useState<PersonCardDto | null>(null);
  /** 이름 · 영문 이름 · 생일 초안 — 인사 초안과 따로 둔다(값의 출처가 다르다: detail.self · 사람 카드). */
  const [idDraft, setIdDraft] = useState<Partial<Record<IdentityKey, string>>>({});
  /** 지금 카드를 기다리는 사람 — 빨리 넘겨 고르면 늦게 온 앞사람 카드가 새 사람 칸을 채우지 않게. */
  const cardFor = useRef<string | null>(null);
  const [teams, setTeams] = useState<Array<{ id: string; name: string }>>([]);
  const [mode, setMode] = useState<'detail' | 'hire'>('detail');
  const [hire, setHire] = useState<HireForm>(EMPTY_HIRE);
  const [issued, setIssued] = useState<{ password: string; annualDays: number | null } | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const load = useCallback(async (q?: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await listEmployees(q ? { search: q } : undefined);
      setRows(data.rows);
      setMissing(data.missingHireDate);
    } catch (err) {
      const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code ?? '';
      setError(ERRORS[code] ?? '직원 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    // 팀 목록은 근무제 화면과 같은 API 를 쓴다 — 목록을 새로 만들지 않는다.
    listWorkSchedules().then((d) => setTeams(d.teams)).catch(() => { /* 배정 칸만 못 쓴다 */ });
  }, []);

  const openDetail = async (userId: string) => {
    // 다른 사람으로 넘어가면 앞사람 카드를 바로 걷는다(같은 사람 재조회는 칸이 깜빡이지 않게 둔다).
    if (userId !== cardFor.current) setCard(null);
    cardFor.current = userId;
    setSelected(userId);
    setMode('detail');
    setDone(null);
    setError(null);
    try {
      const d = await getEmployee(userId);
      setDetail(d);
      setHrDraft({});
      setIdDraft({});
      // 사람 카드는 곁가지다 — 못 읽어도 상세는 그대로 그린다.
      fetchPersonCard(userId)
        .then((c) => { if (cardFor.current === userId) setCard(c); })
        .catch(() => { if (cardFor.current === userId) setCard(null); });
    } catch (err) {
      const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code ?? '';
      setError(ERRORS[code] ?? '직원 정보를 불러오지 못했습니다.');
    }
  };

  const hrValue = (key: keyof EmployeeDetailDto['hr']): string => {
    if (key in hrDraft) return hrDraft[key] ?? '';
    const v = detail?.hr[key];
    return v === null || v === undefined ? '' : String(v);
  };

  const idValue = (key: IdentityKey): string => {
    if (key in idDraft) return idDraft[key] ?? '';
    if (key === 'name') return detail?.self.name ?? '';
    const v = card?.person[key];
    return v === null || v === undefined ? '' : String(v);
  };
  const dirty = Object.keys(hrDraft).length > 0 || Object.keys(idDraft).length > 0;

  const onSaveHr = async () => {
    if (!selected || !dirty) return;
    // ★ 서버가 400 으로 막기 전에 화면이 먼저 말한다 — 이름은 비울 수 없고, 생일은 MM-DD.
    if ('name' in idDraft && !(idDraft.name ?? '').trim()) {
      setDone(null); setError('이름은 비울 수 없습니다.'); return;
    }
    if ('birthday' in idDraft && (idDraft.birthday ?? '').trim() && !BIRTHDAY_RE.test((idDraft.birthday ?? '').trim())) {
      setDone(null); setError('생일은 MM-DD 형식으로 적어 주세요 (예: 03-14).'); return;
    }
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      const body: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(hrDraft)) {
        body[k] = k === 'jobLevel' ? (v ? Number(v) : null) : (v || null);
      }
      // 고친 칸만 싣는다 — 이름은 비울 수 없어 문자열 그대로, 영문 이름 · 생일은 비우면 null(§17.2 권장).
      if ('name' in idDraft) body.name = (idDraft.name ?? '').trim();
      if ('nameEn' in idDraft) body.nameEn = (idDraft.nameEn ?? '').trim() || null;
      if ('birthday' in idDraft) body.birthday = (idDraft.birthday ?? '').trim() || null;
      const res = await updateEmployeeHr(selected, body);
      const savedMsg = res.annualDays === null ? '저장했습니다.' : `저장했습니다 — 올해 연차 ${res.annualDays}일로 다시 계산됐습니다.`;
      setDone(savedMsg);
      setHrDraft({});
      setIdDraft({});
      await Promise.all([load(search), openDetail(selected)]);
      // 재조회(openDetail)가 알림을 지운다 — 다시 읽은 뒤에도 「저장했습니다」가 남게 한 번 더 세운다(2026-09-13 e2e 실측).
      setDone(savedMsg);
    } catch (err) {
      const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code ?? '';
      setError(ERRORS[code] ?? '저장하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  };

  const onHire = async () => {
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      const res = await hireEmployee({
        name: hire.name.trim(),
        email: hire.email.trim(),
        employeeNumber: hire.employeeNumber.trim() || null,
        hireDate: hire.hireDate || null,
        position: hire.position.trim() || null,
        employmentType: hire.employmentType || null,
        teamId: hire.teamId || null,
      });
      setIssued({ password: res.initialPassword, annualDays: res.annualDays });
      setHire(EMPTY_HIRE);
      await load(search);
    } catch (err) {
      const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code ?? '';
      setError(ERRORS[code] ?? '입사 처리를 하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="hra">
      <header className="hra__head">
        <div>
                    {/* ★ 제목은 **메뉴 이름과 같아야 한다** — 인사 트리에서 이 화면을 여는 사람은
              메뉴에 적힌 이름을 보고 누른다(2026-08-27 사용자 지적). */}
          <h1 className="hra__title">직원 명부</h1>
          <p className="hra__sub">업무 › 인사 › 직원 관리 · {rows.length}명 · 인사 정보와 계정을 한 화면에서 봅니다</p>
        </div>
        <button
          type="button"
          className="hra__btn hra__btn--primary"
          onClick={() => { setMode('hire'); setIssued(null); setDone(null); setError(null); }}
        >
          <Plus size={14} aria-hidden />입사 처리
        </button>
      </header>

      {missing > 0 && (
        <p className="hra__error" role="status">
          <AlertCircle size={14} aria-hidden />
          입사일이 없는 사람이 {rows.length}명 중 {missing}명입니다 — 연차를 계산할 수 없어 잔여가 “미설정”입니다.
        </p>
      )}
      {error && <p className="hra__error" role="alert"><AlertCircle size={14} aria-hidden />{error}</p>}
      {done && !error && <p className="hra__ok" role="status"><Check size={14} aria-hidden />{done}</p>}

      <div className="hra__split">
        <aside className="hra__aside" aria-label="직원 목록">
          <label className="hra__field">
            <span>이름 · 사번 검색</span>
            <span className="hra__searchrow">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') void load(search); }}
              />
              <button type="button" className="hra__btn" onClick={() => { void load(search); }} aria-label="검색">
                <Search size={14} aria-hidden />
              </button>
            </span>
          </label>
          <ul className="hra__versions">
            {loading && <li className="hra__dim">불러오는 중…</li>}
            {!loading && rows.length === 0 && <li className="hra__dim">직원이 없습니다.</li>}
            {rows.map((r) => (
              <li key={r.userId} className={selected === r.userId ? 'is-current' : undefined}>
                <button type="button" className="hra__linkbtn" onClick={() => { void openDetail(r.userId); }}>
                  <b>{r.name}{r.employmentStatus !== 'ACTIVE' ? ` · ${EMPLOYMENT_STATUS_LABEL[r.employmentStatus]}` : ''}</b>
                  <span>
                    {r.teamName ?? '회사 직속'} · {r.employeeNumber ?? '사번 없음'} ·
                    {r.hireDate ? ` 연차 ${r.annualDays}일` : ' 입사일 없음'}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </aside>

        <section className="hra__main">
          {mode === 'hire' ? (
            <>
              <h2 className="hra__rulehead">입사 처리 <span className="hra__dim">계정 + 인사 + 연차를 한 번에</span></h2>
              {issued ? (
                <div className="hra__rule">
                  <h3 className="hra__rulehead">만들었습니다 — 초기 비밀번호는 지금 한 번만 보입니다</h3>
                  <p className="hra__pw"><code>{issued.password}</code></p>
                  <p className="hra__note">
                    저장하지 않으므로 다시 볼 수 없습니다. 본인에게 전달하고 첫 로그인에서 바꾸게 하세요.
                    {issued.annualDays !== null && <> 올해 연차는 <b>{issued.annualDays}일</b>로 계산됐습니다.</>}
                  </p>
                  <div className="hra__actions">
                    <button type="button" className="hra__btn" onClick={() => { setIssued(null); setMode('detail'); }}>목록으로</button>
                    <button type="button" className="hra__btn" onClick={() => setIssued(null)}>한 명 더</button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="hra__form">
                    <label className="hra__field"><span>이름</span>
                      <input type="text" value={hire.name} onChange={(e) => setHire({ ...hire, name: e.target.value })} /></label>
                    <label className="hra__field"><span>이메일</span>
                      <input type="email" value={hire.email} onChange={(e) => setHire({ ...hire, email: e.target.value })} /></label>
                    <label className="hra__field"><span>사번</span>
                      <input type="text" value={hire.employeeNumber} onChange={(e) => setHire({ ...hire, employeeNumber: e.target.value })} /></label>
                    <label className="hra__field"><span>입사일</span>
                      <input type="date" value={hire.hireDate} onChange={(e) => setHire({ ...hire, hireDate: e.target.value })} /></label>
                    {/* 같은 `position` 을 여기서만 「직급」이라 불렀다 — 이름을 「직위」로 맞춘다(판 2).
                        레벨 칸은 뺐다: 레벨은 직위가 갖고 있다. */}
                    <label className="hra__field"><span>직위</span>
                      <JobGradeSelect value={hire.position} manage onChange={(v) => setHire({ ...hire, position: v })} /></label>
                    <label className="hra__field"><span>고용형태</span>
                      <select value={hire.employmentType} onChange={(e) => setHire({ ...hire, employmentType: e.target.value })}>
                        {employmentTypes.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                      </select></label>
                    <label className="hra__field"><span>소속 팀</span>
                      <select value={hire.teamId} onChange={(e) => setHire({ ...hire, teamId: e.target.value })}>
                        <option value="">회사 직속</option>
                        {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                      </select></label>
                  </div>
                  <p className="hra__note">
                    역할은 <b>STAFF 로 고정</b>입니다 — 인사 담당은 역할을 올릴 수 없습니다. 이미 있는 계정의
                    비밀번호를 바꾸는 것도 여기가 아니라 관리 콘솔의 일입니다(사람을 들이는 일과 계정을 여는 일은 무게가 다릅니다).
                  </p>
                  <div className="hra__actions">
                    <button type="button" className="hra__btn" onClick={() => setMode('detail')}>취소</button>
                    <button
                      type="button"
                      className="hra__btn hra__btn--primary"
                      onClick={() => { void onHire(); }}
                      disabled={busy || !hire.name.trim() || !hire.email.trim()}
                    >
                      <Save size={14} aria-hidden />{busy ? '만드는 중…' : '입사 처리'}
                    </button>
                  </div>
                </>
              )}
            </>
          ) : !detail ? (
            <p className="hra__note">왼쪽에서 사람을 고르면 인사 정보와 계정을 함께 봅니다.</p>
          ) : (
            <>
              {/* 머리 — 사진 + 이름 + 소속 (2026-09-13 · §17). 종전의 이름 한 줄(h2)을 사람 머리로 바꿨다 —
                  사진을 **여기서** 바꿔야 해서다(본인은 이제 못 바꾼다). 단추는 전사를 보는 사람에게만 뜬다. */}
              <PersonHead
                userId={selected ?? ''}
                name={detail.self.name}
                avatarUrl={card?.person.avatarUrl ?? detail.self.avatarUrl}
                subtitle={`${detail.hr.divisionName ? `${detail.hr.divisionName} › ` : ''}${detail.hr.teamName ?? '회사 직속'}`}
                canEditAvatar={card?.scope === 'ALL' || (card?.scope === 'SELF' && detail.editable.HR)}
                onAvatarChange={() => {
                  // 사진만 바뀌었다 — 카드만 다시 읽는다(상세를 다시 열면 고치던 초안이 날아간다).
                  const id = selected;
                  if (id) fetchPersonCard(id).then((c) => { if (cardFor.current === id) setCard(c); }).catch(() => { /* 옛 사진이 남을 뿐 */ });
                }}
              />

              <section className="hra__rule" aria-label="본인이 고치는 것">
                <h3 className="hra__rulehead">본인이 고치는 것 <span className="hra__dim">내 프로필</span></h3>
                <ul className="hra__rulelist">
                  <li>연락처 · {detail.self.phone ?? '—'}{detail.self.extension ? ` · 내선 ${detail.self.extension}` : ''}</li>
                </ul>
                <p className="hra__note">이 묶음은 {detail.editable.SELF ? '본인이라 고칠 수 있습니다.' : '본인만 고칩니다 — 관리자도 손대지 않습니다.'}</p>
              </section>

              <h3 className="hra__rulehead">인사 담당이 고치는 것</h3>
              <div className="hra__form">
                {/* 2026-09-13 · §17.2 — 본인이 못 고치게 된 세 칸. 사진은 위 머리에서 바꾼다. */}
                <label className="hra__field"><span>이름</span>
                  <input
                    type="text" value={idValue('name')} maxLength={50} disabled={!detail.editable.HR}
                    onChange={(e) => setIdDraft({ ...idDraft, name: e.target.value })}
                  /></label>
                <label className="hra__field"><span>영문 이름</span>
                  <input
                    type="text" value={idValue('nameEn')} maxLength={100} placeholder="Hyunwoo Kim"
                    disabled={!detail.editable.HR || !card}
                    onChange={(e) => setIdDraft({ ...idDraft, nameEn: e.target.value })}
                  /></label>
                <label className="hra__field"><span>생일</span>
                  <input
                    type="text" value={idValue('birthday')} maxLength={5} placeholder="MM-DD"
                    disabled={!detail.editable.HR || !card}
                    onChange={(e) => setIdDraft({ ...idDraft, birthday: e.target.value })}
                  /></label>
                <label className="hra__field"><span>입사일</span>
                  <input
                    type="date" value={hrValue('hireDate')} disabled={!detail.editable.HR}
                    onChange={(e) => setHrDraft({ ...hrDraft, hireDate: e.target.value })}
                  /></label>
                <label className="hra__field"><span>근속</span>
                  <input type="text" value={detail.hr.tenureLabel ?? '미설정'} readOnly /></label>
                <label className="hra__field"><span>올해 연차</span>
                  <input type="text" value={detail.hr.annualDays === null ? '미설정' : `${detail.hr.annualDays}일`} readOnly /></label>
                <label className="hra__field"><span>직위</span>
                  <JobGradeSelect
                    value={hrValue('position')} disabled={!detail.editable.HR} manage
                    onChange={(v) => setHrDraft({ ...hrDraft, position: v })}
                  /></label>
                <label className="hra__field"><span>고용형태</span>
                  <select
                    value={hrValue('employmentType')} disabled={!detail.editable.HR}
                    onChange={(e) => setHrDraft({ ...hrDraft, employmentType: e.target.value })}
                  >
                    <option value="">미설정</option>
                    {employmentTypes.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                  </select></label>
                <label className="hra__field"><span>재직상태</span>
                  <select
                    value={hrValue('employmentStatus')} disabled={!detail.editable.HR}
                    onChange={(e) => setHrDraft({ ...hrDraft, employmentStatus: e.target.value })}
                  >
                    {Object.entries(EMPLOYMENT_STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select></label>
                <label className="hra__field"><span>적용 근무제</span>
                  <input type="text" value={`${detail.schedule.name} · ${detail.schedule.baseStart}–${detail.schedule.baseEnd}`} readOnly /></label>
              </div>
              <div className="hra__actions">
                <button type="button" className="hra__btn" onClick={() => { setHrDraft({}); setIdDraft({}); }} disabled={!dirty || busy}>되돌리기</button>
                <button
                  type="button" className="hra__btn hra__btn--primary"
                  onClick={() => { void onSaveHr(); }}
                  disabled={!detail.editable.HR || !dirty || busy}
                >
                  <Save size={14} aria-hidden />{busy ? '저장 중…' : '저장'}
                </button>
              </div>

              <section className="hra__rule" aria-label="회사 관리자가 고치는 것">
                <h3 className="hra__rulehead">회사 관리자가 고치는 것 <span className="hra__dim">여기서는 보기만</span></h3>
                <ul className="hra__rulelist">
                  <li>이메일 · {detail.account.email}</li>
                  <li>사번 · {detail.account.employeeNumber ?? '—'} <span className="hra__dim">로그인 식별자</span></li>
                  <li>역할 · {detail.account.role}</li>
                  <li>계정 · {detail.account.isActive ? '사용 중' : '비활성'}</li>
                </ul>
                <p className="hra__note">
                  이메일 · 사번 · 역할 · 비밀번호 · 계정 활성은 <b>관리 콘솔의 사용자 관리</b>에서 고칩니다.
                  같은 칸에 길을 둘 내면 “누가 고쳤나”가 두 곳으로 갈립니다.
                </p>
              </section>

              <p className="hra__note">인사 변경은 감사에 남습니다.</p>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
